import React, { useState, useMemo } from 'react';
import * as Icons from 'lucide-react';
import { Input } from './Input';
import { Search } from 'lucide-react';
import styles from './PersonaIconPicker.module.css';
import { safeCap } from '../helpers/stringUtils';

// Curated list of icons that work well for personas
const PERSONA_ICONS = [
  'code',
  'terminal',
  'braces',
  'file-code',
  'git-branch',
  'shield',
  'shield-check',
  'lock',
  'key',
  'eye',
  'server',
  'database',
  'container',
  'cloud',
  'network',
  'file-text',
  'book-open',
  'pen-tool',
  'clipboard-check',
  'list-checks',
  'bug',
  'wrench',
  'settings',
  'cog',
  'tool',
  'zap',
  'rocket',
  'target',
  'award',
  'star',
  'users',
  'user',
  'user-cog',
  'user-check',
  'briefcase',
  'layers',
  'layout',
  'grid',
  'box',
  'package',
  'search',
  'search-code',
  'microscope',
  'scan',
  'filter',
  'gauge',
  'activity',
  'trending-up',
  'bar-chart',
  'line-chart',
  'accessibility',
  'globe',
  'monitor',
  'smartphone',
  'tablet',
  'cpu',
  'hard-drive',
  'memory-stick',
  'wifi',
  'bluetooth',
  'sparkles',
  'wand',
  'lightbulb',
  'brain',
  'puzzle',
  'check-circle',
  'alert-triangle',
  'info',
  'help-circle',
  'message-square',
];

interface PersonaIconPickerProps {
  value: string;
  onChange: (icon: string) => void;
}

export const PersonaIconPicker: React.FC<PersonaIconPickerProps> = ({ value, onChange }) => {
  const [search, setSearch] = useState('');

  const filteredIcons = useMemo(() => {
    if (!search) return PERSONA_ICONS;
    return PERSONA_ICONS.filter((icon) => icon.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  // Convert kebab-case to PascalCase for lucide-react
  const getIconComponent = (iconName: string) => {
    const pascalCase = iconName
      .split('-')
      .map((part) => safeCap(part))
      .join('');
    return (Icons as any)[pascalCase] || Icons.Sparkles;
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchWrapper}>
        <Search size={14} className={styles.searchIcon} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search icons..."
          className={styles.searchInput}
        />
      </div>
      <div className={styles.grid}>
        {filteredIcons.map((iconName) => {
          const IconComponent = getIconComponent(iconName);
          const isSelected = value === iconName;
          return (
            <button
              key={iconName}
              type="button"
              className={`${styles.iconButton} ${isSelected ? styles.selected : ''}`}
              onClick={() => onChange(iconName)}
              title={iconName}
            >
              <IconComponent size={20} />
            </button>
          );
        })}
      </div>
      {filteredIcons.length === 0 && <p className={styles.noResults}>No icons found</p>}
    </div>
  );
};
